import { useState } from 'react';
import { CarouselModal } from '@anytag/shared-molecules';
import { useQueryHelper, useToggleState } from '@anytag/shared-hooks';
import { cn, formatIntNumber } from '@anytag/shared-utils';
import { PostInsightForm, PostInsightStatus } from '@anytag/shared-organisms';
import { Anchor, Icon, RenderDataWithFallback, SocialIcon, SocialIconProps } from '@anytag/shared-atoms';
import {
  CampaignSocialMediaType,
  EngagementPostForInsightCheck,
  useEngagementPostMetricsForInsightCheckQuery
} from '@anytag/graphql';
import { usePostsInsightFormProps } from './hooks';

interface PostDetailsProps {
  postIndex: number;
  totalPosts: number;
  campaignId: number;
  post: EngagementPostForInsightCheck;
  onMoveToPost?: (index: number) => void;
}

export const PostDetails = ({
  postIndex,
  totalPosts,
  campaignId,
  onMoveToPost,
  post: { id: postId, influencerName }
}: PostDetailsProps) => {
  const [imageIndex, setImageIndex] = useState<number>(0);
  const { t, navigate, enqueueSnackbar } = useQueryHelper();
  const { status: isOpening, toggleStatus: toggleModal } = useToggleState();
  const { data, loading } = useEngagementPostMetricsForInsightCheckQuery({
    variables: { postId },
    onError: (error) => {
      enqueueSnackbar(t(error.message), { variant: 'error' });
      navigate({ to: '/posts-insight/$id', params: { id: campaignId.toString() } });
    }
  });

  const postData = data?.engagementPostMetricsForInsightCheck || null;
  const socialMedia = postData?.socialMedia as CampaignSocialMediaType;
  const postInsightUrls = postData?.insightUrls || [];
  const postInsightUrlsLength = postInsightUrls.length;
  const postInsightFormProps = usePostsInsightFormProps({
    postId,
    postData,
    currentImageUrl: postInsightUrls.at(imageIndex) || ''
  });

  const handleClickNextImage = () => {
    setImageIndex(imageIndex + 1);
  };

  const handleClickPrevImage = () => {
    setImageIndex(imageIndex - 1);
  };

  return (
    <RenderDataWithFallback loading={loading}>
      {postData ? (
        <>
          <div className="flex flex-[1_1_auto] flex-col">
            <div className={styles.header}>
              <SocialIcon size="18px" type={socialMedia as SocialIconProps['type']} className="mr-1" />
              <Anchor
                target="_blank"
                className="text-base"
                label={influencerName}
                to="/engagement/$id/posts"
                params={{ id: campaignId.toString() }}
                search={{ popup: String(postId) }}
              />
              <PostInsightStatus status={postData.status} className="my-0 mr-auto ml-2 px-2 py-1.5 text-sm" />
              {onMoveToPost ? (
                <div className="flex items-center">
                  <button
                    className={styles.headerButton}
                    disabled={postIndex === 1}
                    onClick={() => onMoveToPost(postIndex - 1)}
                  >
                    <Icon size={10} icon="caret-left" variant="gray" />
                  </button>
                  <span className="px-6 text-base">
                    {`${formatIntNumber(postIndex)}/${formatIntNumber(totalPosts)}`}
                  </span>
                  <button
                    className={styles.headerButton}
                    disabled={postIndex === totalPosts}
                    onClick={() => onMoveToPost(postIndex + 1)}
                  >
                    <Icon size={10} icon="caret-right" variant="gray" />
                  </button>
                </div>
              ) : null}
            </div>

            <div className="flex h-full overflow-hidden">
              <div className={styles.section}>
                {postInsightUrlsLength > 1 ? (
                  <>
                    {imageIndex > 0 ? (
                      <button
                        onClick={handleClickPrevImage}
                        className={cn(styles.headerButton, styles.imageButton, 'left-6')}
                      >
                        <Icon size={10} icon="caret-left" variant="gray" />
                      </button>
                    ) : null}

                    {imageIndex !== postInsightUrlsLength - 1 ? (
                      <button
                        className={cn(styles.headerButton, styles.imageButton, 'right-6')}
                        onClick={handleClickNextImage}
                      >
                        <Icon size={10} icon="caret-right" variant="gray" />
                      </button>
                    ) : null}
                  </>
                ) : null}
                <img
                  className={styles.image}
                  onClick={toggleModal}
                  alt={t<string>('Posts Insight')}
                  src={postInsightUrls[imageIndex] || ''}
                />
              </div>
              <div className={styles.section}>
                <PostInsightForm {...postInsightFormProps} />
              </div>
            </div>
          </div>

          <CarouselModal
            open={isOpening}
            onClose={toggleModal}
            onClickNext={handleClickNextImage}
            onClickPrev={handleClickPrevImage}
            siblingPostId={{
              prevId: imageIndex - 1 >= 0 ? String(imageIndex - 1) : null,
              nextId: imageIndex + 1 < postInsightUrlsLength ? String(imageIndex + 1) : null
            }}
          >
            <div className="flex h-screen max-h-[80vh] max-w-[80vh] items-center justify-center">
              <img
                alt={t<string>('Posts Insight')}
                className="max-h-full max-w-full"
                src={postInsightUrls[imageIndex] || ''}
              />
            </div>
          </CarouselModal>
        </>
      ) : null}
    </RenderDataWithFallback>
  );
};
const styles = {
  header: cn('flex h-12 flex-none items-center border-b-border pr-6 pl-5'),
  headerButton: cn(
    'border-none bg-transparent p-0',
    'disabled:cursor-not-allowed disabled:opacity-50',
    'not-disabled:hover:cursor-pointer not-disabled:hover:opacity-50'
  ),
  section: cn('relative flex-[50%_0_0] overflow-auto [&+&]:border-l-border'),
  image: cn('absolute top-0 left-1/2 max-h-full -translate-x-1/2 cursor-pointer'),
  imageButton: 'absolute top-6 z-1'
};
